import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { mapProps } from '../../../../../../../util/hocs';
import Button from '../../../../../../../components/global/Button';
import { AlbumFolderTags } from '../../../../../../../graphql/queries/album-folder-tags';
import Placeholder from '../IdentityTagSelection/Placeholder';
import {
  getTagsByType,
  getSortedAlbumFolderGradeTags,
} from '../../../../../../../util/album-folder-tags';
import {
  identityAlbumFolderTagsCreate as identityAlbumFolderTagsCreateMutation,
  identityAlbumFolderTagsDelete as identityAlbumFolderTagsDeleteMutation,
} from '../../../../../../../graphql/mutations/album-folder-tags';
import Alert from '../../../../../../../components/global/Alert';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import getIdentityAlbumFolderTags, {
  getIdentityAlbumFolderTagsQuery,
} from '../../graphql/queries/getIdentityAlbumFolderTags';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../../../../../constants/album-folder-tags';
import { IDENTITY_GENDER } from '../../../../../../../constants/identity';
import Select from '../../../../../../../components/global/Select';
import { getIdentityName } from '../../../../../../../util/identity';

const NO_GRADE = 'NO_GRADE';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      uniform_enabled: PropTypes.bool,
      uniform_numbered: PropTypes.bool,
      levels: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            teams: PropTypes.shape({
              records: PropTypes.arrayOf(
                PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string,
                  coach_name: PropTypes.string,
                }),
              ),
            }),
          }),
        ),
      }),
      gender: PropTypes.oneOf(Object.values(IDENTITY_GENDER)),
    }),
  ),
  identityAlbumFolderTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      album_folder_tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      uniform_album_folder_tag_level: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      uniform_album_folder_tag_level_team: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      jersey_number: PropTypes.string,
    }),
  ),
  identityAlbumFolderTagsCreate: PropTypes.func.isRequired,
  identityAlbumFolderTagsDelete: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
  completeBtnText: PropTypes.string,
};

const GradeTagSelection = ({
  tags,
  identity,
  identityAlbumFolderTags,
  identityAlbumFolderTagsCreate,
  identityAlbumFolderTagsDelete,
  onComplete,
  onError,
  className,
  completeBtnText = 'Continue',
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (!tags?.length || !identityAlbumFolderTags) {
    return <Placeholder />;
  }

  const classes = classNames({
    'identity-tag-selection-form': true,
    [className]: !!className,
  });

  // Avoid deleting uniform related tags.
  const identityAlbumFolderGradeTags = identityAlbumFolderTags.filter(
    identityAlbumFolderTag =>
      identityAlbumFolderTag.album_folder_tag.type ===
      ALBUM_FOLDER_TAG_TYPE.GRADE,
  );

  const sortedAlbumFolderGradeTags = getSortedAlbumFolderGradeTags(tags);

  const onFormSubmit = async values => {
    if (values.gradeTagId === NO_GRADE) {
      onComplete?.(identity);
    }

    const input = {
      identity_id: identity.id,
      album_folder_tags: [{ album_folder_tag_id: values.gradeTagId }],
    };

    setSubmitting(true);
    setSubmitError('');

    try {
      const identityAlbumFolderTagIds = identityAlbumFolderGradeTags.map(
        ({ id }) => id,
      );

      if (identityAlbumFolderTagIds.length > 0 && input) {
        // For brevity, simply delete all existing tags and re-create them -- it's
        // harmless and saves a lot of logic determining what changed
        // and what didn't.
        await identityAlbumFolderTagsDelete(identityAlbumFolderTagIds);
      }

      if (input) {
        await identityAlbumFolderTagsCreate(input);
      }
      setSubmitting(false);
      onComplete?.(identity);
    } catch (error) {
      setSubmitError(getErrorString(error));
      setSubmitting(false);
      onError?.(error);
    }
  };

  const [identityAlbumFolderGradeTag] = identityAlbumFolderGradeTags;

  return (
    <Formik
      initialValues={{
        gradeTagId: identityAlbumFolderGradeTag?.album_folder_tag.id ?? '',
      }}
      onSubmit={onFormSubmit}
      render={({ values, errors, handleSubmit, handleChange }) => {
        const formikErrors = Object.values(errors);

        return (
          <form onSubmit={handleSubmit} className={classes}>
            <div className="identity-tag-selection-tag-select">
              <Select
                id="grade-tags-selection"
                name="gradeTagId"
                label={`Select ${getIdentityName(identity)}'s Grade`}
                required
                value={values.gradeTagId}
                onChange={handleChange}
                size="sm"
              >
                <option value="">Please select your grade&hellip;</option>
                {sortedAlbumFolderGradeTags.map(tag => (
                  <option value={tag.id} key={tag.id}>
                    {tag.name}
                  </option>
                ))}
                <option value={NO_GRADE}>
                  I am a Teacher / Don&rsquo;t know.
                </option>
              </Select>
            </div>
            <div className="identity-tag-selection-actions">
              {(submitError || formikErrors.length > 0) && (
                <Alert type="danger">
                  {submitError ||
                    formikErrors.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                </Alert>
              )}

              <Button
                btnBlock
                type="submit"
                disabled={submitting || !values.gradeTagId}
              >
                {submitting ? 'Loading...' : completeBtnText}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

GradeTagSelection.propTypes = propTypes;

const withAlbumFolderTags = graphql(AlbumFolderTags, {
  options: ({ match, albumFolder }) => ({
    variables: {
      id: albumFolder?.id ?? match?.params?.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder } = {} }) => ({
    tags: getTagsByType(
      ALBUM_FOLDER_TAG_TYPE.GRADE,
      albumFolder?.tags?.records,
    ),
  }),
});

const withIdentityAlbumFolderTagsCreate = graphql(
  identityAlbumFolderTagsCreateMutation,
  {
    props: ({ mutate, ownProps: { identityId, albumFolderId } }) => ({
      identityAlbumFolderTagsCreate: input =>
        mutate({
          variables: {
            input,
          },
          refetchQueries: [
            {
              query: getIdentityAlbumFolderTagsQuery,
              variables: {
                identityId,
                albumFolderId,
              },
            },
          ],
        }),
    }),
  },
);

const withIdentityAlbumFolderTagsDelete = graphql(
  identityAlbumFolderTagsDeleteMutation,
  {
    props: ({ mutate }) => ({
      identityAlbumFolderTagsDelete: ids =>
        mutate({
          variables: {
            input: {
              ids,
            },
          },
        }),
    }),
  },
);

export default compose(
  withRouter,
  withAlbumFolderTags,
  mapProps(({ ...props }) => ({
    ...props,
    identityId: props.identity?.id,
    albumFolderId: props.albumFolder?.id,
  })),
  getIdentityAlbumFolderTags,
  mapProps(({ data, ...props }) => ({
    ...props,
    identityAlbumFolderTags: data?.identity?.album_folder_tags?.records,
  })),
  withIdentityAlbumFolderTagsCreate,
  withIdentityAlbumFolderTagsDelete,
)(GradeTagSelection);
